import React from 'react';
import { Textarea as ShadcnTextarea } from '@/components/ui/textarea';
import { Label as ShadcnLabel } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface TextAreaFieldProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'ref'> {
    label?: string;
    error?: string;
    description?: string;
}

export const TextAreaField = React.forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
    ({ className, label, error, description, id, ...props }, ref) => {
        // Generate a unique ID if not provided and we have a label
        const textareaId = id || (label ? `textarea-${Math.random().toString(36).substr(2, 9)}` : undefined);

        const textareaComponent = (
            <ShadcnTextarea
                ref={ref}
                id={textareaId}
                className={cn(
                    // Glue's textarea styling
                    'min-h-[80px] px-2 py-2 rounded border border-border bg-background text-foreground',
                    'focus:ring-1 focus:ring-ring focus:border-ring',
                    'resize-vertical',
                    error && 'border-destructive focus:border-destructive focus:ring-destructive',
                    className,
                )}
                {...props}
            />
        );

        if (label) {
            return (
                <div className="flex flex-col space-y-2">
                    <ShadcnLabel htmlFor={textareaId} className={cn(error && 'text-destructive')}>
                        {label}
                    </ShadcnLabel>
                    {textareaComponent}
                    {error && <p className="text-sm text-destructive">{error}</p>}
                    {description && !error && <p className="text-sm text-muted-foreground">{description}</p>}
                </div>
            );
        }

        return textareaComponent;
    },
);

TextAreaField.displayName = 'TextAreaField';
